import React from 'react';
import { Clock3, Flag } from 'lucide-react';
import ChartToolbarButton from '../Chart/ChartToolbarButton';
import { ChartStyleToggle, ChartToolbar, ChartToolbarValue } from '../Chart/ChartToolbar';
import { ThemeToggleGroup } from '../Theme';
import type { StrategyChartKind, StrategyChartTimeframe } from './strategySymbolChartData';
import type { StrategyChartLegendEntry } from './strategySymbolChartPresentation';

export type StrategyChartStyle = 'candles' | 'line';

interface StrategySymbolChartControlsProps {
  symbol: string;
  label: string;
  strategy: StrategyChartKind;
  timeframe: StrategyChartTimeframe;
  onTimeframeChange?: (timeframe: '5min' | '1d') => void;
  chartStyle: StrategyChartStyle;
  showMarkers: boolean;
  showExtendedHours: boolean;
  legend: StrategyChartLegendEntry[];
  onChartStyleChange: (style: StrategyChartStyle) => void;
  onToggleMarkers: () => void;
  onToggleExtendedHours: () => void;
}

const StrategySymbolChartControls: React.FC<StrategySymbolChartControlsProps> = ({
  symbol,
  label,
  strategy,
  timeframe,
  onTimeframeChange,
  chartStyle,
  showMarkers,
  showExtendedHours,
  legend,
  onChartStyleChange,
  onToggleMarkers,
  onToggleExtendedHours,
}) => (
  <ChartToolbar
    leading={(
      <div className="flex shrink-0 items-baseline gap-1.5">
        <span className="text-xs font-bold text-white">{symbol}</span>
        <span className="text-[9px] uppercase tracking-wider text-muted">{label}</span>
      </div>
    )}
    legend={(
      <>
        {legend.map((item) => (
          <span key={item.label} className="inline-flex shrink-0 items-center gap-1 text-[9px] uppercase tracking-wide text-muted">
            <span className="w-4 h-0.5 rounded-full" style={{ backgroundColor: item.color }} />
            {item.label}
          </span>
        ))}
      </>
    )}
    chartStyle={(
      <ChartStyleToggle
        value={chartStyle}
        onChange={onChartStyleChange}
        showLine={timeframe !== '1d'}
      />
    )}
    actions={(
      <>
        {strategy === 'MORB' && (
          <ChartToolbarButton
            size="compact"
            iconOnly
            onClick={onToggleMarkers}
            icon={<Flag className="h-3.5 w-3.5" />}
            selected={showMarkers}
            label={timeframe === '1d'
              ? showMarkers ? 'Hide buy/close signals' : 'Show buy/close signals'
              : showMarkers ? 'Hide potential signal markers' : 'Show potential signal markers'}
            title={timeframe === '1d'
              ? showMarkers ? 'Hide buy/close signals' : 'Show buy/close signals'
              : showMarkers ? 'Hide potential signal markers' : 'Show potential signal markers'}
          />
        )}
        {timeframe !== '1d' && (
          <ChartToolbarButton
            size="compact"
            onClick={onToggleExtendedHours}
            selected={showExtendedHours}
            label={showExtendedHours ? 'Show regular market hours only' : 'Show extended hours'}
            title={showExtendedHours ? 'Show regular market hours only' : 'Show extended hours'}
            icon={<Clock3 className="h-3.5 w-3.5" />}
          >
            {showExtendedHours ? 'EXT' : 'RTH'}
          </ChartToolbarButton>
        )}
      </>
    )}
    trailing={onTimeframeChange ? (
      <ThemeToggleGroup aria-label="Chart timeframe" className="shrink-0 gap-px">
        <ChartToolbarButton
          size="compact"
          segment="first"
          label="5 minute bars"
          selected={timeframe === '5min'}
          onClick={() => onTimeframeChange('5min')}
        >5m</ChartToolbarButton>
        <ChartToolbarButton
          size="compact"
          segment="last"
          label="Daily candles"
          selected={timeframe === '1d'}
          onClick={() => onTimeframeChange('1d')}
        >1D</ChartToolbarButton>
      </ThemeToggleGroup>
    ) : <ChartToolbarValue>{timeframe}</ChartToolbarValue>}
  />
);

export default StrategySymbolChartControls;
